/// <amd-module name="Comments/Viewer/Components/CommentViewerToolbar" />
import * as CommonResources from "Comments/Common/Resources";
import { CommentReactionSelector } from "Comments/Reactions/Components/CommentReactionSelector";
import { ICommentViewerToolbarProps } from "Comments/Viewer/Components/CommentViewerToolbar.Props";
import * as Resources from "Comments/Viewer/Resources.ViewerToolbar";
import * as React from "react";
import { ObservableValue } from "VSS/Core/Observable";
import { VssComponent, IVssComponentContext } from "VSS/Platform/Layout";
import { IVssThemeService } from "VSS/Platform/Theming";
import { Button } from "VSSUI/Button";
import { Dialog } from "VSSUI/Dialog";
import { FocusWithin, IFocusWithinStatus } from "VSSUI/FocusWithin";
import { FocusZone, FocusZoneDirection } from "VSSUI/FocusZone";
import { IMenuItem, MoreButton } from "VSSUI/Menu";
import { Observer } from "VSSUI/Observer";
import * as VSSUI_Resources from "VSSUI/Resources.Widgets";
import { css } from "VSSUI/Util";
import { copyToClipboard } from "VSSUI/Utils/ClipboardUtils";

export interface ICommentViewerToolbarState {
    actionButtonsClassName: string;
}

/**
 * Toolbar for comment viewer.
 */
export class CommentViewerToolbar extends VssComponent<ICommentViewerToolbarProps, ICommentViewerToolbarState> {
    private _moreActions: IMenuItem[];
    private _moreButtonRef = React.createRef<MoreButton>();
    private _showDeleteDialog = new ObservableValue<boolean>(false);
    private _themeService: IVssThemeService;

    constructor(props: ICommentViewerToolbarProps, context: IVssComponentContext) {
        super(props, context);

        this._themeService = this.context.pageContext.getService("IVssThemeService");

        this.state = { actionButtonsClassName: "" };

        this._updateMoreActions(props);
    }

    public render(): JSX.Element {
        const { className, editDisabled, reactionSelectorProps, editButtonRef, baseUrl, readonly, canEdit, formatDisabled } = this.props;
        const { actionButtonsClassName } = this.state;

        return (
            <Observer themeService={this._themeService}>
                {() => {
                    const shouldRenderMoreActionsButton = this._moreActions.length > 0;
                    const shouldRenderReactionsButton = !readonly && Boolean(reactionSelectorProps);
                    const shouldRenderEditButton = !readonly && canEdit;
                    const shouldRenderFormatButton =
                        !formatDisabled && this._themeService.getCurrentTheme().isDark && this.props.showDarkThemeFormat;
                    const canRenderActionButtons =
                        baseUrl !== undefined ||
                        shouldRenderMoreActionsButton ||
                        shouldRenderReactionsButton ||
                        shouldRenderEditButton ||
                        shouldRenderFormatButton;

                    const setSize =
                        Number(shouldRenderMoreActionsButton) +
                        Number(shouldRenderReactionsButton) +
                        Number(shouldRenderEditButton) +
                        Number(shouldRenderFormatButton != undefined);

                    let currentPosInSet = 1;

                    const buttonsToRender = (focusStatus: IFocusWithinStatus) => {
                        const buttons = [];
                        if (baseUrl) {
                            buttons.push(
                                <Button
                                    iconProps={{ iconName: "Link" }}
                                    ariaLabel={Resources.CopyLink}
                                    disabled={editDisabled}
                                    onClick={this._copyLink}
                                    onFocus={focusStatus.onFocus}
                                    onBlur={focusStatus.onBlur}
                                    tooltipProps={{ text: Resources.CopyLink }}
                                    ref={editButtonRef}
                                    excludeTabStop={shouldRenderReactionsButton}
                                    role="menuitem"
                                    subtle
                                    key={"permalink-" + this.props.commentId}
                                />
                            );
                        }
                        if (shouldRenderReactionsButton) {
                            buttons.push(
                                <CommentReactionSelector
                                    {...reactionSelectorProps}
                                    onAddReactionButtonClick={this._onAddReactionButtonClick}
                                    onSelectionDismiss={this._onReactionSelectionDismiss}
                                    onFocus={focusStatus.onFocus}
                                    onBlur={focusStatus.onBlur}
                                    ariaPosInSet={currentPosInSet++}
                                    ariaSetSize={setSize}
                                    key={"reactions-" + this.props.commentId}
                                />
                            );
                        }
                        if (shouldRenderEditButton) {
                            buttons.push(
                                <Button
                                    className="comment-edit-button"
                                    iconProps={{ iconName: "Edit" }}
                                    ariaLabel={Resources.Edit}
                                    disabled={editDisabled}
                                    onClick={this._onStartEdit}
                                    onFocus={focusStatus.onFocus}
                                    onBlur={focusStatus.onBlur}
                                    tooltipProps={{ text: Resources.Edit }}
                                    ref={editButtonRef}
                                    excludeTabStop={shouldRenderReactionsButton}
                                    role="menuitem"
                                    ariaPosInSet={currentPosInSet++}
                                    ariaSetSize={setSize}
                                    subtle
                                    key={"edit-" + this.props.commentId}
                                />
                            );
                        }
                        if (shouldRenderFormatButton) {
                            buttons.push(
                                <Observer showDarkThemeFormat={this.props.showDarkThemeFormat}>
                                    {(observerProps: { showDarkThemeFormat: boolean }) => {
                                        const ariaLabel = observerProps.showDarkThemeFormat
                                            ? Resources.HideDarkThemeFormat
                                            : Resources.ShowDarkThemeFormat;
                                        return (
                                            <Button
                                                iconProps={
                                                    observerProps.showDarkThemeFormat ? { iconName: "Hide2" } : { iconName: "RedEye" }
                                                }
                                                ariaLabel={ariaLabel}
                                                onClick={() =>
                                                    (this.props.showDarkThemeFormat.value = !this.props.showDarkThemeFormat.value)
                                                }
                                                onFocus={focusStatus.onFocus}
                                                onBlur={focusStatus.onBlur}
                                                tooltipProps={{
                                                    text: ariaLabel
                                                }}
                                                role="menuitem"
                                                ariaPosInSet={currentPosInSet++}
                                                ariaSetSize={setSize}
                                                subtle
                                                key={"format-" + this.props.commentId}
                                            />
                                        );
                                    }}
                                </Observer>
                            );
                        }
                        if (shouldRenderMoreActionsButton) {
                            buttons.push(
                                <MoreButton
                                    className="comment-context-menu-button"
                                    contextualMenuProps={{
                                        menuProps: {
                                            id: "comment-context-menu",
                                            items: this._moreActions.filter((action) => Boolean(action))
                                        }
                                    }}
                                    onExpand={this._onContextMenuExpand}
                                    onCollapse={this._onContextMenuDismiss}
                                    onFocus={focusStatus.onFocus}
                                    onBlur={focusStatus.onBlur}
                                    ref={this._moreButtonRef}
                                    iconProps={{ iconName: "More" }}
                                    tooltipProps={{ text: VSSUI_Resources.MoreActions }}
                                    excludeTabStop={shouldRenderReactionsButton || shouldRenderEditButton}
                                    role="menuitem"
                                    ariaPosInSet={currentPosInSet++}
                                    ariaSetSize={setSize}
                                    hideDropdownIcon
                                    key={"more-" + this.props.commentId}
                                />
                            );
                        }

                        return buttons;
                    };

                    if (!canRenderActionButtons) {
                        return null;
                    }

                    return (
                        <FocusWithin updateStateOnFocusChange>
                            {(focusStatus: IFocusWithinStatus) => {
                                const toolbarClassName = css(
                                    "comment-viewer-toolbar",
                                    className,
                                    actionButtonsClassName,
                                    focusStatus.hasFocus && "toolbar-focused"
                                );

                                return (
                                    <div className={toolbarClassName} role="menubar">
                                        <FocusZone direction={FocusZoneDirection.Horizontal}>
                                            <div className="flex-row">{buttonsToRender(focusStatus)}</div>
                                        </FocusZone>
                                        {this._renderDeleteConfirmationDialog()}
                                    </div>
                                );
                            }}
                        </FocusWithin>
                    );
                }}
            </Observer>
        );
    }

    private _updateMoreActions(nextProps: ICommentViewerToolbarProps): void {
        const { canEdit, readonly, getPermalink } = nextProps;

        this._moreActions = [];
        if (getPermalink) {
            this._moreActions.push({
                id: "permalink",
                onActivate: this._copyLink,
                iconProps: { iconName: "Link" },
                text: Resources.CopyLink
            } as IMenuItem);
        }
        if (canEdit && !readonly) {
            this._moreActions.push({
                id: "delete",
                onActivate: this._showDeleteConfirmationDialog,
                iconProps: { iconName: "Delete" },
                text: Resources.Delete
            } as IMenuItem);
        }
    }

    private _onStartEdit = (): void => {
        const { commentId, onStartEdit } = this.props;
        onStartEdit(commentId);
    };

    private _onContextMenuDismiss = (): void => {
        this.setState({ actionButtonsClassName: "" });
    };

    private _onReactionSelectionDismiss = (): void => {
        const {
            reactionSelectorProps: { onSelectionDismiss }
        } = this.props;

        this._onContextMenuDismiss();
        onSelectionDismiss && onSelectionDismiss();
    };

    private _onAddReactionButtonClick = (): void => {
        const {
            reactionSelectorProps: { onAddReactionButtonClick }
        } = this.props;

        onAddReactionButtonClick && onAddReactionButtonClick();
        this._makeActionButtonsVisible();
    };

    private _onContextMenuExpand = (): void => {
        const { commentId, onMoreClick } = this.props;

        onMoreClick && onMoreClick(commentId);
        this._makeActionButtonsVisible();
    };

    private _makeActionButtonsVisible = (): void => {
        this.setState({ actionButtonsClassName: "menu-expanded" });
    };

    private _copyLink = (): void => {
        const { commentId, baseUrl } = this.props;
        const commentUrl = baseUrl + "#" + commentId;
        copyToClipboard(String(commentUrl));
    };

    private _showDeleteConfirmationDialog = (): void => {
        this._showDeleteDialog.value = true;
    };

    private _renderDeleteConfirmationDialog(): JSX.Element | undefined {
        const deleteCancelClassName = "cancel-delete-comment";
        return (
            <Observer isDialogOpen={this._showDeleteDialog}>
                {(props: { isDialogOpen: boolean }) =>
                    props.isDialogOpen && (
                        <Dialog
                            titleProps={{ text: Resources.ConfirmDeleteComment }}
                            onDismiss={this._dismissConfirmDeleteDialog}
                            lightDismiss={false}
                            defaultActiveElement={`.${deleteCancelClassName}`}
                            footerButtonProps={[
                                {
                                    text: CommonResources.Cancel,
                                    onClick: this._dismissConfirmDeleteDialog,
                                    className: deleteCancelClassName
                                },
                                { text: Resources.Delete, danger: true, onClick: this._onConfirmDeleteClick }
                            ]}
                        />
                    )
                }
            </Observer>
        );
    }

    private _dismissConfirmDeleteDialog = (): void => {
        const { commentId, onConfirmDelete } = this.props;

        this._showDeleteDialog.value = false;
        requestAnimationFrame(() => this._moreButtonRef && this._moreButtonRef.current && this._moreButtonRef.current.focus());
        onConfirmDelete(commentId, false);
    };

    private _onConfirmDeleteClick = (): void => {
        const { commentId, onConfirmDelete } = this.props;

        this._showDeleteDialog.value = false;
        onConfirmDelete(commentId, true);
    };
}

VssComponent.register("comment-viewer-toolbar", CommentViewerToolbar);
